// 일정 관리(v3.26) — 날짜별 그룹 목록: 시간 · 카테고리 · 분야 · 제목.
import { useEffect, useRef } from "react";
import { cn } from "@/lib/cn";
import type { ScheduleItem } from "@/api/types";
import { dayKey, fieldBadgeClass, fmtDayHeading, fmtTime, groupByDay, isSameDay } from "./utils";
import { Repeat } from "lucide-react";

interface Props {
  items: ScheduleItem[];
  selectedKey: string | null; // 캘린더에서 고른 날짜 — 해당 그룹으로 스크롤
  onOpen: (item: ScheduleItem) => void;
}

export default function ScheduleList({ items, selectedKey, onOpen }: Props) {
  const groups = groupByDay(items);
  const boxRef = useRef<HTMLDivElement>(null);

  // 선택 날짜(없으면 오늘 이후 첫 그룹)로 스크롤
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const targetKey =
      selectedKey ?? groups.find(([k]) => k >= dayKey(new Date()))?.[0] ?? null;
    if (!targetKey) return;
    const el = box.querySelector<HTMLElement>(`[data-day="${targetKey}"]`);
    if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, items.length]);

  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        이 달에 등록된 일정이 없습니다.
      </p>
    );
  }

  const today = new Date();
  return (
    <div ref={boxRef} className="space-y-3 pb-6">
      {groups.map(([key, list]) => (
        <section key={key} data-day={key} className="scroll-mt-2">
          <h3
            className={cn(
              "mb-1 border-b border-border pb-1 text-sm font-bold",
              selectedKey === key && "text-primary",
            )}
          >
            {fmtDayHeading(key)}
            {isSameDay(new Date(`${key}T00:00:00`), today) && (
              <span className="ml-1.5 rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                오늘
              </span>
            )}
          </h3>
          {list.map((s) => (
            <button
              key={s.id}
              onClick={() => onOpen(s)}
              className="flex w-full items-center gap-2 rounded-lg px-1.5 py-2 text-left hover:bg-muted"
            >
              <span className="w-[92px] shrink-0 text-[13px] tabular-nums text-foreground">
                {fmtTime(s.start_at)}~{fmtTime(s.end_at)}
              </span>
              <span className="shrink-0 rounded-full border border-foreground/60 px-2 py-0.5 text-[11px] font-semibold">
                {s.category}
              </span>
              <span
                className={cn(
                  "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  fieldBadgeClass(s.field),
                )}
              >
                {s.field}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm">{s.title}</span>
              {s.repeat_group && (
                <Repeat className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="반복 일정" />
              )}
            </button>
          ))}
        </section>
      ))}
    </div>
  );
}
